import { pdfHeaderContext, pdfHeaderText, pdfHeaderTag } from "../pdfTagName";
import type { State } from "../../State";
import type Consumer from "../../models/Consumer";
import type Floor from "../../models/Floor";
import type Room from "../../models/Room";
import type Device from "../../models/Device";
import { ChannelType, channelTypeInfo } from "../../models/ChannelType";
import { KnxSceneBehaviour } from "../../models/KnxSceneInfo";
import { label, sorted, type TechnicalReport, type ReportSection, type ReportTable } from "./types";
import { deviceMetrics } from "./equipment";

/** Collects channels and installation data, including decentralized room actuators. */
export function actorReport(app: State, showImage = true): TechnicalReport {
  const sections: ReportSection[] = [];
  const devices: Device[] = sorted(app.allActors());
  devices.push(...sorted(app.allButtons()).filter(button => button.channels.asArray().some(channel => channel.isActorChannel)));
  for (const device of devices) {
    if (channelTypeInfo(device.channelType).isSupply || device.channelType === ChannelType.Lan) continue;
    const channels = device.channelsSortetAndFilteredForListspdf();
    const rows: string[][] = [];
    for (const channel of channels) {
      if (device.isButton && !channel.isActorChannel) continue;
      const consumer = channel.getConnectedConsumer();
      if (device.isActor && !consumer && !channel.fuse && !channel.terminal) continue;
      rows.push([channel.getChannelNameForUI(), label(consumer),
        pdfHeaderTag(consumer?.fuse) || "", pdfHeaderTag(consumer?.terminal) || ""]);
    }
    const metrics = deviceMetrics(device.template);
    const actorContext = { ...pdfHeaderContext(), useTags: true };
    const actorTitle = pdfHeaderText(device, actorContext);
    const section: ReportSection = { title: actorTitle, frontImage: showImage ? device.frontImage : undefined, hierarchy: [label(device.parent), actorTitle], tables: [],
      detailsTitle: "Spezifikation",
      details: [
        { label: "Tag", value: pdfHeaderTag(device, actorContext) },
        { label: "Name", value: device.name },
        { label: "Produkt", value: device.template?.name || "Ohne Produktauswahl" },
        { label: "Physikalische Adresse", value: device.fullPhysicalAddress },
        { label: "Teilungseinheiten", value: `${metrics.te}TE` },
        { label: "Busstrom", value: `${metrics.bus}mA` },
      ] };
    section.tables.push({ columns: ["Kanal", "Verbraucher", "Sicherung", "Klemme"], rows });
    sections.push(section);
  }
  return { sections };
}

/** Reads a consumer's connection data without changing the model or its grouping. */
function consumerConnectionRow(consumer: Consumer, app: State): string[] {
  const channel = consumer.getConnectedChannel();
  return [pdfHeaderTag(consumer, { ...pdfHeaderContext(), useTags: true }), consumer.name, app.translate(`channelType.${consumer.channelType}`, consumer.channelType),
    pdfHeaderTag(channel?.mappedChannel) || pdfHeaderTag(channel) || "", pdfHeaderTag(consumer.terminal) || "", pdfHeaderTag(consumer.fuse) || "", consumer.cableType?.name || ""];
}

/** Starts a section per floor so every page has an unambiguous floor header, including nested consumers. */
export function consumerReport(app: State, groupByRoom = true): TechnicalReport {
  const byFloor = new Map<Floor | undefined, Consumer[]>();
  for (const consumer of sorted(app.allConsumers())) {
    const floor = consumer.parentRoom?.parentFloor;
    const consumers = byFloor.get(floor) ?? [];
    consumers.push(consumer);
    byFloor.set(floor, consumers);
  }
  const groups = [...byFloor].sort(([first], [second]) => label(first).localeCompare(label(second), "de", { numeric: true }));
  const sections: ReportSection[] = [];
  for (const [floor, consumers] of groups) {
    const floorTitle = label(floor);
    sections.push({ title: floorTitle || "Ohne Etagenzuordnung", hierarchy: [floorTitle, ""],
      tables: consumerTables(app, consumers, groupByRoom) });
  }
  return { sections };
}

/** Groups consumers by room identity and orders headings by their formatted display text. */
function consumerTables(app: State, consumers: Consumer[], groupByRoom: boolean): ReportTable[] {
  const columns = ["Tag", "Name", "Funktion", "Aktorkanal", "Klemme", "Sicherung", "Kabel"];
  if (!groupByRoom) return [{ columns, rows: consumers.map(consumer => consumerConnectionRow(consumer, app)) }];
  const rooms = new Map<Room | undefined, Consumer[]>();
  for (const consumer of consumers) {
    const room = consumer.parentRoom;
    const rows = rooms.get(room) ?? [];
    rows.push(consumer);
    rooms.set(room, rows);
  }
  const groups = Array.from(rooms, ([room, rows]) => ({ title: pdfHeaderText(room) || "Ohne Raumzuordnung", rows }));
  groups.sort((a, b) => a.title.localeCompare(b.title, app.settings.language, { numeric: true }));
  return groups.map(group => ({ title: group.title, columns, rows: group.rows.map(consumer => consumerConnectionRow(consumer, app)) }));
}

/** Describes the physical cable destination independently of which actuator model is used. */
function cableTarget(consumer: Consumer): string {
  if (consumer.noCable) return consumer.isDali ? "Kein separates Kabel erforderlich (DALI)" : "Kein Kabel erforderlich";
  const via = consumer.getTerminalViaConsumer();
  if (via) {
    const consumerLabel = pdfHeaderText(via);
    return [consumerLabel, consumer.terminalViaWireName].filter(Boolean).join(" · ");
  }
  const actor = consumer.getConnectedChannel()?.parent;
  return actor ? label(actor.className === "Actor" ? actor.parent : actor) : label(consumer.terminal?.parent?.parent);
}

/** Lists the actual planned cable runs, with optional per-cabinet counts and bus topology. */
export function cablePullReport(app: State, includeCounts: boolean, pagePerRoom = false): TechnicalReport {
  const sections: ReportSection[] = [];
  const counts = new Map<string, Map<string, number>>();
  const knxSection: ReportSection = { title: "KNX Kabel", tables: [] };
  for (const floor of sorted(app.floors.asArray())) {
    const floorSection: ReportSection = { title: label(floor), hierarchy: [label(floor), ""], tables: [] };
    for (const room of sorted(floor.rooms.asArray())) {
      const rows: string[][] = [];
      for (const consumer of sorted(room.allConsumers())) {
        if (consumer.noCable) continue;
        rows.push([consumer.tag, consumer.name, cableTarget(consumer), consumer.cableType?.name || ""]);
        if (!consumer.noCable && consumer.cableType && !consumer.getTerminalViaConsumer()) {
          const destination = cableTarget(consumer) || "Ohne Zielzuordnung";
          const cables = counts.get(destination) ?? new Map<string, number>();
          cables.set(consumer.cableType.name, (cables.get(consumer.cableType.name) ?? 0) + 1);
          counts.set(destination, cables);
        }
      }
      const buttonRows: string[][] = [];
      for (const button of sorted(room.allButtons)) {
        const area = app.topologies?.asArray().find(item => String(item.areaNumber) === String(button.areaAddress));
        const line = area?.lines.asArray().find(item => String(item.lineNumber) === String(button.lineAddress));
        const lineLabel = line?.name
          ? `Linie ${button.lineAddress} (${line.name})`
          : `Linie ${button.lineAddress}`;
        buttonRows.push([button.tag, button.name, `Bereich ${button.areaAddress} · ${lineLabel}`, button.getCableType()?.name || "KNX-Kabel"]);
      }
      const columns = ["Tag", "Name", "Ziel", "Kabel"];
      const tables: ReportTable[] = [];
      if (rows.length) tables.push({ layoutKey: "cable-pull", columns, rows });
      const roomTitle = pdfHeaderText(room);
      if (buttonRows.length) knxSection.tables.push({ layoutKey: "cable-pull", parentGroupTitle: label(floor),
        groupTitle: roomTitle, columns, rows: buttonRows });
      if (!tables.length) continue;
      if (pagePerRoom) {
        sections.push({ title: `${label(floor)} / ${label(room)}`, hierarchy: [label(floor), roomTitle], tables });
      } else {
        for (const table of tables) table.groupTitle = roomTitle;
        floorSection.tables.push(...tables);
      }
    }
    if (floorSection.tables.length) sections.push(floorSection);
  }
  if (includeCounts) {
    for (const [destination, cables] of counts) {
      sections.push({ title: `Kabelanzahl · ${destination}`, tables: [{ columns: ["Kabeltyp", "Anzahl"],
        rows: [...cables].map(([name, count]) => [name, String(count)]) }] });
    }
  }
  if (knxSection.tables.length) sections.push(knxSection);
  return { sections };
}

/** Documents the cable roots and each wire once; empty wires remain visible. */
export function cableReport(app: State, wires: boolean): TechnicalReport {
  const consumers = sorted(app.allConsumers());
  const roots = consumers.filter(consumer => !consumer.getTerminalViaConsumer());
  if (!wires) return { sections: [{ title: "Kabelrouting", tables: [{ columns: ["Verbraucher", "Name", "Klemme"],
    rows: roots.map(consumer => [pdfHeaderTag(consumer), consumer.name, pdfHeaderTag(consumer.terminal) || ""]) }] }] };
  const sections: ReportSection[] = [];
  for (const root of roots) {
    const cable = root.cableType;
    if (!cable) continue;
    const downstream = consumers.filter(consumer => consumer.getTerminalViaConsumer()?.id === root.id);
    const rows: string[][] = [];
    for (const wire of cable.wireNames) {
      const connected = downstream.filter(consumer => (consumer.getTerminalViaConnection()?.name || consumer.terminalViaWireName) === wire);
      const targets = connected.map(label);
      if (wire === cable.wireNames[2]) targets.unshift(label(root));
      rows.push([wire, targets.join("\n")]);
    }
    for (const consumer of downstream) {
      const wire = consumer.getTerminalViaConnection()?.name || consumer.terminalViaWireName || "Ohne Aderzuordnung";
      if (!cable.wireNames.includes(wire)) rows.push([wire, label(consumer)]);
    }
    sections.push({ title: label(root), notes: [label(cable), `Klemme: ${pdfHeaderTag(root.terminal) || "–"}`], tables: [{ columns: ["Ader", "Verbraucher"], rows }] });
  }
  return { sections };
}

/** Reads scene participants and configured behaviour without modifying product metadata during export. */
export function sceneReport(app: State, multiplexers: boolean): TechnicalReport {
  const sections: ReportSection[] = [];
  const scenes = sorted(app.allScenes()).filter(scene => scene.isMultiplexer === multiplexers);
  scenes.sort((a, b) => a.sceneNumber - b.sceneNumber);
  for (const scene of scenes) {
    const title = `${scene.sceneNumber} · ${label(scene)}`;
    const section: ReportSection = { title, hierarchy: multiplexers ? ["", ""] : [title, ""], tables: [] };
    section.tables.push({ columns: ["Taste / Kanal", "Name", "Ort"], rows: sorted(scene.getConnectedButtonChannels()).map(channel => [pdfHeaderTag(channel), channel.name, label(channel.parent?.parent)]) });
    const rows: string[][] = [];
    for (const consumer of sorted(scene.getConnectedConsumers())) {
      const channel = consumer.getConnectedChannel();
      const behaviour: string[] = [];
      for (const connection of consumer.connections.asArray()) {
        if (connection.getPartner(consumer)?.id !== scene.id) continue;
        for (const item of connection.knxSceneInfo?.behaviours ?? []) {
          const device = channel?.parent as Device | undefined;
          const value = device?.isActor ? KnxSceneBehaviour.getCurrentValueDisplay(device, item) : item.data?.a?.Value;
          behaviour.push(`${item.label}: ${value || item.data?.a?.Value || ""}`);
        }
      }
      rows.push([label(consumer), label(consumer.parent), pdfHeaderTag(channel?.mappedChannel) || pdfHeaderTag(channel) || "", behaviour.join("\n")]);
    }
    section.tables.push({ columns: ["Verbraucher", "Ort", "Aktorkanal", "Szenenwerte"], rows });
    if (multiplexers) {
      const columns = ["Tag / Kanal", "Name", "Ort", "Aktorkanal", "Szenenwerte"];
      const inputs = section.tables[0].rows.map(row => [...row, "", ""]);
      section.tables = [
        { title: "Tasten / Kanäle", columns, rows: inputs },
        { title: "Verbraucher", columns, rows: sorted(scene.getConnectedConsumers()).map((consumer, index) =>
          [pdfHeaderTag(consumer), consumer.name, rows[index][1], rows[index][2], rows[index][3]]) },
      ];
    }
    sections.push(section);
  }
  return { sections, ...(multiplexers ? { keepSectionsTogether: true } : {}) };
}
